import {
  isEmpty,
  isNil,
  max,
  omitBy,
  round,
  sortBy,
  sumBy,
  values,
} from 'lodash';
import * as Yup from 'yup';
import { HIGH_VOLUME_IMPORT_VEHICLE } from 'utils/constant';
import { arrayKeyIndexes } from 'utils/helpers';

const defaultInitialValues = {
  vehicleTypeGroupId: '',
  cmsVehicleTypeId: '',
  vehicleTypeId: '',
  vehicleTypeRadio: '',
  vehicleCategory: '',
  vehicleMake: '',
  vehicleModel: '',
  bodyStyle: '',
  vehicleYear: '',
  vehicleModelVariant: '',
  vehicleSeat: '',
  hireVehicle: false,
  vehicleDimensionLength: 0,
  vehicleDimensionHeight: 0,
  vehicleDimensionWidth: 0,
  vehicleDimensionWeight: 0,
  confirmedVehicleDetails: false,
  trailerRadio: '',
  trailerLength: 0,
  trailerHeight: 0,
  roofLuggage: false,
  tooWide: false,
  carryingDangerousGoods: false,
  trailerDescription: '',
  // https://formik.org/docs/guides/arrays#arrays
  vehicleAddonsOption: [],
  showVehicleTypeRadio: false,
  showVehicleModelVariant: false,
  showAttachmentBlock: false,
  showVehicleFormContent: true,
  vehicleDescription: '',
  vehicleTemplateId: '',
  vehicleRegistration: '',
};

export const isRegisteredVehiclesOnly = (vehicleConfig, user) => {
  const {
    registeredVehiclesOnly = false,
    registeredVehiclesOnlyClientTypes = [],
  } = vehicleConfig || {};
  const { client_type_id: clientTypeId = null } = user || {};

  return (
    registeredVehiclesOnly &&
    registeredVehiclesOnlyClientTypes &&
    clientTypeId &&
    registeredVehiclesOnlyClientTypes.includes(clientTypeId)
  );
};

export const getInitialValues = (state) => {
  // Currently only support 1 vehicle
  const vehicleForm =
    state.ui?.sealink.ferryGrouping[0]?.values.vehicleForm?.vehicles[0];
  const initialValues = {
    ...defaultInitialValues,
    ...vehicleForm,
  };

  // Select the first vehicle type radio by default
  if (!initialValues.vehicleTypeRadio) {
    const firstRadio = state.sealink.vehicle_type_groups
      .find(
        (group) => group.vehicle_types.length > 1 || group.name === 'Motorbike'
      )
      ?.vehicle_types[0].toString();
    if (firstRadio) {
      initialValues.vehicleTypeRadio = firstRadio;
    }
  }

  if (isRegisteredVehiclesOnly(state.sealink?.vehicle_config, state.user)) {
    initialValues.showVehicleFormContent = false;
  }

  return initialValues;
};

export const roundSize = (value) => round(value, 2);

export const getTotalDimension = (vehicleDimension, formValue, attachments) => {
  const allDimensions = {
    vehicle: {
      length: Number(vehicleDimension.length || 0),
      height: Number(vehicleDimension.height || 0),
    },
  };

  let heightOfVehicleWithRoofLuggage = allDimensions.vehicle.height;

  // If attachment block is visible
  if (formValue.showAttachmentBlock) {
    // If Roof luggage checked
    if (formValue.roofLuggage && formValue.selectedRoofLuggage) {
      const roofLuggageDetail = attachments.find(
        (item) => Number(formValue.selectedRoofLuggage) === item.id
      );

      allDimensions.roofLuggage = {
        height: Number(roofLuggageDetail?.height_value),
      };

      heightOfVehicleWithRoofLuggage =
        allDimensions.vehicle.height + allDimensions.roofLuggage.height;
    }

    // If add-on checked
    const addonIds = arrayKeyIndexes(formValue.vehicleAddonsOption);

    if (addonIds.length) {
      const addonDetails = attachments.filter((item) =>
        addonIds.includes(item.id)
      );
      const addonLength = sumBy(addonDetails, (item) =>
        Number(item.length_value)
      );
      const addonHeight = sumBy(addonDetails, (item) =>
        Number(item.height_value)
      );

      allDimensions.addon = {
        length: addonLength,
        height: addonHeight,
      };
    }
  }

  // If has trailer
  if (formValue.trailerRadio) {
    allDimensions.trailer = {
      length: Number(formValue.trailerLength || 0),
      height: Number(formValue.trailerHeight || 0),
    };
  }

  return {
    height: roundSize(
      max([heightOfVehicleWithRoofLuggage, allDimensions.trailer?.height || 0])
    ),
    length: roundSize(sumBy(values(allDimensions), (item) => item.length)),
  };
};

export const sortVehicleTypes = (types) => {
  const sorted = sortBy(types, [(o) => o.name]);

  // Make sure Motorhome is above Motorbike
  // Discussion: https://bitbucket.org/team-sealink/stg-website-platform/pull-requests/1405#comment-286046124
  const findIndexByName = (name) =>
    sorted.findIndex((item) => item.name.toLowerCase() === name);
  const motorhomeIndex = findIndexByName('motorhome');
  const motorbikeIndex = findIndexByName('motorbike');

  if (motorhomeIndex >= 0 && motorbikeIndex >= 0) {
    const vehicleTypeGroupTemp = sorted[motorhomeIndex];

    sorted[motorhomeIndex] = sorted[motorbikeIndex];
    sorted[motorbikeIndex] = vehicleTypeGroupTemp;
  }

  return sorted;
};

export const setVehicleDimensionsFunction =
  (setFieldValue) =>
  ({ width, height, length, weight }) => {
    setFieldValue('vehicleDimensionHeight', height ? Number(height) : 0);
    setFieldValue('vehicleDimensionLength', length ? Number(length) : 0);
    setFieldValue('vehicleDimensionWidth', width ? Number(width) : 0);
    setFieldValue('vehicleDimensionWeight', weight ? Number(weight) : 0);
  };

export const vehicleFormSchema = Yup.lazy((value) => {
  const rules = {
    vehicleTypeId: Yup.string().required('Vehicle type is required'),
    carryingDangerousGoods: Yup.boolean().oneOf([false]),
    trailerDescription: Yup.string().when('trailerRadio', {
      is: (val) => val !== undefined && val !== '' && Number(val) !== 0,
      then: (schema) => schema.required('Trailer description is required'),
    }),
    vehicleCategory: Yup.string().when('hireVehicle', {
      is: true,
      then: (schema) => schema.required('Vehicle category is required'),
    }),
    vehicleRegistration: Yup.string()
      .nullable()
      .when('showVehicleRegistration', {
        is: true,
        then: (schema) => schema.required('Vehicle registration is required'),
      }),
  };

  // If not a hired car or saved car, then additional vehicle details would be required
  if (
    value.vehicleSegmentCode &&
    !value.hireVehicle &&
    !value.vehicleTemplateId
  ) {
    rules.vehicleMake = Yup.string().required('Vehicle make is required');
    rules.vehicleModel = Yup.string().required('Vehicle model is required');
    rules.vehicleYear = Yup.string().required('Vehicle year is required');

    if (value.showVehicleModelVariant) {
      rules.vehicleModelVariant = Yup.string().required(
        'Vehicle variant is required'
      );
    }
  }

  // If high volumn import car, no detail need
  if (
    value.vehicleSegmentCode &&
    (value.vehicleMake === HIGH_VOLUME_IMPORT_VEHICLE ||
      value.vehicleModel === HIGH_VOLUME_IMPORT_VEHICLE)
  ) {
    rules.vehicleMake = undefined;
    rules.vehicleModel = undefined;
    rules.vehicleYear = undefined;
    rules.vehicleModelVariant = undefined;
  }

  // If needs to specify the vehicle type
  if (value.showVehicleTypeRadio) {
    rules.vehicleTypeRadio = Yup.string().required('Vehicle type is required');
  }

  // Block the process if the vehicle is too wide
  if (value.tooWide) {
    rules.tooWide = Yup.boolean().oneOf([false]);
  }

  if (value.showVehicleFormContent) {
    rules.confirmedVehicleDetails = Yup.boolean().oneOf([true]);
  }

  return Yup.object().shape(rules);
});

export const getFormValuesToSubmit = (formValues) => {
  const keysToRemove = [
    'showVehicleTypeRadio',
    'confirmedVehicleDetails',
    'showAttachmentBlock',
    'showVehicleRegistration',
    'showVehicleDimensionSummary',
    'showVehicleBodyDetail',
  ];

  // No attachment
  if (!formValues.showAttachmentBlock) {
    keysToRemove.push(
      'roofLuggage',
      'selectedRoofLuggage',
      'attachmentIds',
      'vehicleAddonsOption'
    );
  }
  if (!formValues.showVehicleTypeRadio) {
    keysToRemove.push('vehicleTypeRadio');
  }
  // No trailer, no trailder description
  if (!formValues.trailerRadio) {
    keysToRemove.push('trailerDescription');
  }
  // Not a hire car, no details.
  if (!formValues.hireVehicle) {
    keysToRemove.push('details', 'vehicleCategory');
  } else {
    keysToRemove.push(
      'vehicleMake',
      'vehicleModel',
      'bodyStyle',
      'vehicleYear',
      'vehicleModelVariant',
      'vehicleSeat'
    );
  }
  if (!formValues.roofLuggage) {
    keysToRemove.push('selectedRoofLuggage');
  }
  // Only normal car get vehicleSegmentCode
  if (!formValues.vehicleSegmentCode) {
    keysToRemove.push(
      'vehicleMake',
      'vehicleModel',
      'bodyStyle',
      'vehicleYear',
      'vehicleModelVariant',
      'vehicleSeat'
    );
  }

  const attachmentIds = arrayKeyIndexes(formValues.vehicleAddonsOption || []);
  if (formValues.roofLuggage && formValues.selectedRoofLuggage) {
    attachmentIds.push(Number(formValues.selectedRoofLuggage));
  }

  const {
    vehicleDimensionWidth: width,
    vehicleDimensionHeight: height,
    vehicleDimensionLength: length,
    vehicleDimensionWeight: weight,
  } = formValues;

  const finalDimensions = omitBy(
    {
      length,
      height,
      width,
      weight,
    },
    (v) => v === 0
  );

  // If the value stays the same or isNil or is empty array,
  // omit it so not get stored in redux
  return omitBy(
    { ...formValues, attachmentIds, finalDimensions },
    (v, k) =>
      defaultInitialValues[k] === v ||
      isNil(v) ||
      (Array.isArray(v) && isEmpty(v)) ||
      keysToRemove.includes(k)
  );
};

// This is for the handling of High Volume Import vehicles
export const addOtherOption = (options) =>
  options.concat({ description: 'Other', code: HIGH_VOLUME_IMPORT_VEHICLE });

// After We get the QT vehicle type id from saved vehicle
// we filter the list of CMS vehicle types by this id (there could be multiple)
// If multiple, check the trailer capability of each of the remaining types
// If at least one of them allows trailer, then we will let the user add a trailer
// By returning that CMS vehicle type id.
export const getVehicleTypeForSavedCar = (qtVehicleTypeId, cmsVehicleTypes) => {
  const list = cmsVehicleTypes.filter(
    (item) => item.qt_vehicle_type_id === qtVehicleTypeId
  );
  const result =
    list.length > 1
      ? list.find((item) => !isEmpty(item.trailer_vehicle_types))
      : list[0];
  if (result) {
    return result.id;
  }
};
